import Colors from '@constants/Colors';
import { Box, CircularProgress, Switch, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState, useMemo, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import PhotoGalleryModal from './PhotoGalleryModal';

const PhotoGalleryContainer = styled(Box)(({ theme }) => ({
    backgroundColor: Colors.DASHBOARD,
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0px 4px 80px 0px rgba(112, 144, 176, 0.14)',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
}));

const PhotoGalleryHeader = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
}));

const PhotoTitle = styled(Typography)(() => ({
    fontFamily: 'LEMON_MILK_Pro, sans-serif',
    fontSize: '25px',
    fontWeight: 500,
    lineHeight: '40px',
    color: Colors.NEUTRAL_WHITE,
}));

const MainImageContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    height: '427px',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '3.333px 3.333px 6.667px 0px rgba(0, 0, 0, 0.07)',
    cursor: 'pointer',
}));

const MainImage = styled('img')(() => ({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
}));

const ThumbnailContainer = styled(Box)(() => ({
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
}));

const Thumbnail = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100px',
    height: '98px',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '3.333px 3.333px 6.667px 0px rgba(0, 0, 0, 0.07)',
    cursor: 'pointer',
    '&:hover': {
        transform: 'scale(1.02)',
        transition: 'transform 0.2s ease',
    },
}));

const ThumbnailImage = styled('img')(() => ({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
}));

const MorePhotosOverlay = styled(Box)(() => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.12)',
    backdropFilter: 'blur(2.5px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: Colors.NEUTRAL_WHITE,
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontWeight: 'bold',
    fontSize: '24px',
    lineHeight: '1',
    textAlign: 'center',
}));

const CustomSwitch = styled(Switch)(({ theme }) => ({
    '& .MuiSwitch-thumb': {
        backgroundColor: Colors.NEUTRAL_WHITE,
    },
    '& .MuiSwitch-track': {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&.Mui-checked .MuiSwitch-track': {
        backgroundColor: Colors.PRIMARY_PURPLE,
    },
}));

const LoadingOverlay = styled(Box)(() => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '12px',
}));

const PhotoGallery = ({ pro, jobOffer, onSubmit }: { pro: any, jobOffer: any, onSubmit?: () => Promise<void> }) => {
    const { setValue } = useFormContext();
    const [isEnabled, setIsEnabled] = useState(jobOffer?.showPhotos ?? true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [loadingImages, setLoadingImages] = useState<Set<number>>(new Set());

    // Get photos from brand first, fallback to pro
    const images = useMemo(() => {
        const source = jobOffer?.brand || jobOffer?.pro || pro;
        const brandPhotos = source?.brandPhotos || [];
        return brandPhotos.map((photo: any) => photo.url).filter(Boolean);
    }, [jobOffer, pro]);

    // Update toggle state when jobOffer changes
    useEffect(() => {
        if (jobOffer?.showPhotos !== undefined) {
            setIsEnabled(jobOffer.showPhotos);
        }
    }, [jobOffer?.showPhotos]);

    // Handle toggle change and submit to backend
    const handleToggleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.checked;
        setIsEnabled(newValue);
        setValue('showPhotos', newValue);
        await onSubmit?.();
    };

    const visibleThumbnails = images.slice(0, 4);
    const remainingCount = images.length - 4;

    const handleImageLoad = (index: number) => {
        setLoadingImages(prev => {
            const newSet = new Set(prev);
            newSet.delete(index);
            return newSet;
        });
    };

    const handleImageLoadStart = (index: number) => {
        setLoadingImages(prev => new Set(prev).add(index));
    };

    const handleMainImageClick = () => {
        setCurrentImageIndex(0);
        setIsModalOpen(true);
    };

    const handleThumbnailClick = (index: number) => {
        if (index === 3 && remainingCount > 0) {
            // Last thumbnail - open modal showing all images
            setCurrentImageIndex(0);
            setIsModalOpen(true);
        } else {
            // Regular thumbnail - set as main image and show in modal
            setCurrentImageIndex(index);
            setIsModalOpen(true);
        }
    };

    // Don't render if no images available
    if (!images || images.length === 0) {
        return null;
    }

    return (
        <>
            <PhotoGalleryContainer>
                <PhotoGalleryHeader>
                    <PhotoTitle>Photos</PhotoTitle>
                    <CustomSwitch
                        checked={isEnabled}
                        onChange={handleToggleChange}
                        size="medium"
                    />
                </PhotoGalleryHeader>
                {isEnabled && (<>
                    {/* Main Image */}
                    <MainImageContainer onClick={handleMainImageClick}>
                        {loadingImages.has(0) && (
                            <LoadingOverlay>
                                <CircularProgress size={40} sx={{ color: Colors.NEUTRAL_WHITE }} />
                            </LoadingOverlay>
                        )}
                        <MainImage
                            src={images[0]}
                            alt="Main office photo"
                            onLoad={() => handleImageLoad(0)}
                            onLoadStart={() => handleImageLoadStart(0)}
                        />
                    </MainImageContainer>

                    {/* Thumbnails */}
                    {images.length > 1 && (
                        <ThumbnailContainer>
                        {visibleThumbnails.map((image, index) => (
                            <Thumbnail
                                key={index}
                                onClick={() => handleThumbnailClick(index)}
                            >
                                {loadingImages.has(index + 1) && (
                                    <LoadingOverlay>
                                        <CircularProgress size={24} sx={{ color: Colors.NEUTRAL_WHITE }} />
                                    </LoadingOverlay>
                                )}
                                <ThumbnailImage
                                    src={image}
                                    alt={`Office photo ${index + 1}`}
                                    onLoad={() => handleImageLoad(index + 1)}
                                    onLoadStart={() => handleImageLoadStart(index + 1)}
                                />
                                {index === 3 && remainingCount > 0 && (
                                    <MorePhotosOverlay>
                                        <div>+{remainingCount}</div>
                                        <div>Photos</div>
                                    </MorePhotosOverlay>
                                )}
                            </Thumbnail>
                        ))}
                        </ThumbnailContainer>
                    )}
                </>)}
            </PhotoGalleryContainer>

            <PhotoGalleryModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                images={images}
                currentIndex={currentImageIndex}
                onIndexChange={setCurrentImageIndex}
            />
        </>
    );
};

export default PhotoGallery;
